<script setup lang="ts">
import { ChevronDown as ChevronDownIcon } from '@lucide/vue'
import { extent } from 'd3-array'
import { scaleLinear } from 'd3-scale'
import { area, curveMonotoneX, line } from 'd3-shape'
import { computed, onMounted, onUnmounted, nextTick, ref, watch } from 'vue'

import { useEventListener } from '@/composables/useEventListener'
import { isMediumScreen, isSmallScreen } from '@/services/uiService'
import {
  findNearestTrackProfilePoint,
  getTrackProfileBreakdown,
  type TrackProfileAnalyses,
  type TrackProfileAnalysis,
  type TrackProfileLeg,
  type TrackProfileLocationMatch,
  type TrackProfileMetricId,
  type TrackProfileVisibleRange,
} from '@/utils/trackProfileAnalysis'

defineOptions({ name: 'ElevationProfileChart' })

// Breakdown bars cover the non-elevation metrics. The chart curve is tinted by whichever
// metric is currently selected (gradient/elevation, surface, or way type).
type ProfileBreakdownMetricId = 'surface' | 'wayType'
type ProfileSection = { metricId: ProfileBreakdownMetricId; categoryId: string }

const props = withDefaults(
  defineProps<{
    analyses: TrackProfileAnalyses
    /** Metric whose legs colour the chart curve. */
    metric?: TrackProfileMetricId
    cursorDistanceM?: number | null
    visibleRangeM?: TrackProfileVisibleRange | null
    activeMetric?: ProfileBreakdownMetricId | null
    highlightedCategoryId?: string | null
    isCalculatingRoute?: boolean
    currentLocation?: TrackProfileLocationMatch | null
    interactionResetKey?: number
    showBreakdowns?: boolean
    /** Hide the surface / way-type breakdowns behind a toggle (default expanded). */
    breakdownsCollapsible?: boolean
    /** Taller, frameless chart with elevation gridlines (dedicated Profile view). */
    tall?: boolean
    /** A→B selection range; when set, draws A (start) and B (end) markers on the curve. */
    segmentRange?: TrackProfileVisibleRange | null
  }>(),
  {
    metric: 'gradient',
    cursorDistanceM: null,
    visibleRangeM: null,
    activeMetric: null,
    highlightedCategoryId: null,
    isCalculatingRoute: false,
    currentLocation: null,
    showBreakdowns: true,
    breakdownsCollapsible: false,
    tall: false,
    segmentRange: null,
  },
)

const emit = defineEmits<{
  'update:cursorDistanceM': [value: number | null]
  'update:visibleRangeM': [value: TrackProfileVisibleRange | null]
  'select:range': [value: TrackProfileVisibleRange]
  'hover:section': [value: ProfileSection | null]
  'cycle:section': [value: ProfileSection]
  'leave:profile': []
}>()

// Elevation shape always comes from the gradient analysis; metric only changes leg colour.
const gradientAnalysis = computed(() => props.analyses.gradient)
const colorLegs = computed<TrackProfileLeg[]>(
  () => props.analyses[props.metric]?.legs ?? gradientAnalysis.value.legs,
)

const interactionScopeRef = ref<HTMLDivElement | null>(null)
const chartContainerRef = ref<HTMLDivElement | null>(null)
const svgRef = ref<SVGSVGElement | null>(null)
const zoomRangeM = ref<TrackProfileVisibleRange | null>(props.visibleRangeM)
const selectionStartDistanceM = ref<number | null>(null)
const selectionEndDistanceM = ref<number | null>(null)
const hoveredSection = ref<ProfileSection | null>(null)
const breakdownsExpanded = ref(false)

const chartWidth = ref(560)
const chartHeight = computed(() => {
  if (props.tall) {
    // Kept compact on phones so the profile fits inside the route sheet's mid snap without
    // enlarging the sheet (the header + tabs already claim most of the height).
    if (isSmallScreen.value) return 140
    if (isMediumScreen.value) return 168
    return 200
  }
  if (isSmallScreen.value) return 97
  if (isMediumScreen.value) return 105
  return 120
})
const MIN_ZOOM_RANGE_METERS = 25
const MIN_CHART_WIDTH = 320
const margin = { top: 12, right: 16, bottom: 24, left: 16 }
let chartResizeObserver: ResizeObserver | null = null
let observedChartContainer: HTMLDivElement | null = null
let chartWidthFrameId: number | null = null
const innerWidth = computed(() => Math.max(chartWidth.value - margin.left - margin.right, 1))
const innerHeight = computed(() => chartHeight.value - margin.top - margin.bottom)

const totalDistanceM = computed(() => Math.max(gradientAnalysis.value.totalDistanceM, 1))
const chartRangeM = computed(() => zoomRangeM.value ?? { start: 0, end: totalDistanceM.value })

const xScale = computed(() =>
  scaleLinear()
    .domain([chartRangeM.value.start, chartRangeM.value.end])
    .range([margin.left, margin.left + innerWidth.value]),
)

const elevationDomain = computed<[number, number]>(() => {
  const values = gradientAnalysis.value.points
    .map((point) => point.ele)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  const [minValue, maxValue] = extent(values)
  if (minValue === undefined || maxValue === undefined) return [0, 1]
  const span = Math.max(maxValue - minValue, 20)
  const padding = Math.max(10, span * 0.1)
  return [minValue - padding, maxValue + padding]
})

const yScale = computed(() =>
  scaleLinear()
    .domain(elevationDomain.value)
    .range([margin.top + innerHeight.value, margin.top]),
)

// Horizontal elevation gridlines (tall variant only), labelled inside the left gutter.
const yGridlines = computed(() => {
  if (!props.tall) return []
  return yScale.value
    .ticks(3)
    .map((value) => ({ value, y: yScale.value(value), label: `${Math.round(value)} m` }))
    .filter((tick) => tick.y >= margin.top && tick.y <= margin.top + innerHeight.value)
})

const chartPoints = computed(() =>
  gradientAnalysis.value.points
    .filter((point) => point.ele !== null)
    .filter(
      (point) =>
        point.distanceM >= chartRangeM.value.start && point.distanceM <= chartRangeM.value.end,
    )
    .map((point) => ({
      x: xScale.value(point.distanceM),
      y: yScale.value(point.ele ?? 0),
      distanceM: point.distanceM,
      ele: point.ele ?? 0,
    })),
)

const visibleLegs = computed(() =>
  colorLegs.value.filter(
    (leg) =>
      leg.endDistanceM >= chartRangeM.value.start && leg.startDistanceM <= chartRangeM.value.end,
  ),
)

const areaPath = computed(() => {
  if (chartPoints.value.length < 2) return ''
  return (
    area<{ x: number; y: number }>()
      .x((point) => point.x)
      .y0(margin.top + innerHeight.value)
      .y1((point) => point.y)
      .curve(curveMonotoneX)(chartPoints.value) ?? ''
  )
})

const linePath = computed(() => {
  if (chartPoints.value.length < 2) return ''
  return (
    line<{ x: number; y: number }>()
      .x((point) => point.x)
      .y((point) => point.y)
      .curve(curveMonotoneX)(chartPoints.value) ?? ''
  )
})

const activePoint = computed(() =>
  props.cursorDistanceM === null || props.cursorDistanceM === undefined
    ? null
    : findNearestTrackProfilePoint(gradientAnalysis.value.points, props.cursorDistanceM),
)

const findActiveLeg = (analysis: TrackProfileAnalysis): TrackProfileLeg | null => {
  if (analysis.legs.length === 0) return null
  const cursorDistanceM = props.cursorDistanceM
  if (cursorDistanceM === null || cursorDistanceM === undefined) return null
  return (
    analysis.legs.find(
      (leg) => cursorDistanceM >= leg.startDistanceM && cursorDistanceM <= leg.endDistanceM,
    ) ?? analysis.legs[analysis.legs.length - 1]
  )
}

const sectionReadout = computed(() => {
  if (!activePoint.value) return null
  const gradientLeg = findActiveLeg(props.analyses.gradient)
  const wayTypeLeg = findActiveLeg(props.analyses.wayType)
  const surfaceLeg = findActiveLeg(props.analyses.surface)

  const chips: string[] = []
  const gradientValue = gradientLeg?.rawValue ?? gradientLeg?.categoryLabel
  if (gradientValue) chips.push(gradientValue)
  if (wayTypeLeg && wayTypeLeg.categoryId !== 'unknown') chips.push(wayTypeLeg.categoryLabel)
  if (surfaceLeg && surfaceLeg.categoryId !== 'unknown') chips.push(surfaceLeg.categoryLabel)

  return {
    distance: formatDistance(activePoint.value.distanceM),
    elevation: activePoint.value.ele !== null ? `${Math.round(activePoint.value.ele)} m` : null,
    chips,
  }
})

const formatPercent = (percent: number) => `${Math.round(percent * 100)}%`

const surfaceBreakdown = computed(() => getTrackProfileBreakdown(props.analyses.surface))
const wayTypeBreakdown = computed(() => getTrackProfileBreakdown(props.analyses.wayType))

const bars = computed(() => [
  { metricId: 'surface' as const, label: 'Surfaces', breakdown: surfaceBreakdown.value },
  { metricId: 'wayType' as const, label: 'Way types', breakdown: wayTypeBreakdown.value },
])

const isBreakdownEmpty = (breakdown: ReturnType<typeof getTrackProfileBreakdown>) =>
  breakdown.length === 0 || breakdown.every((item) => item.id === 'unknown')

const focusedCategoryFor = (metricId: ProfileBreakdownMetricId): string | null => {
  if (hoveredSection.value?.metricId === metricId) return hoveredSection.value.categoryId
  if (props.activeMetric === metricId) return props.highlightedCategoryId
  return null
}

const cursorX = computed(() =>
  xScale.value(
    activePoint.value
      ? Math.max(
          chartRangeM.value.start,
          Math.min(activePoint.value.distanceM, chartRangeM.value.end),
        )
      : chartRangeM.value.start,
  ),
)

const cursorY = computed(() =>
  activePoint.value && activePoint.value.ele !== null ? yScale.value(activePoint.value.ele) : null,
)

const currentLocationMarker = computed(() => {
  const location = props.currentLocation
  if (!location) return null
  if (location.distanceM < chartRangeM.value.start || location.distanceM > chartRangeM.value.end) {
    return null
  }
  return {
    x: xScale.value(location.distanceM),
    y: location.elevationM === null ? null : yScale.value(location.elevationM),
    distanceText: formatDistance(location.distanceM),
    offsetText: `${Math.round(location.distanceFromTrackM)} m from route`,
  }
})

// A and B end markers for an active A→B selection, pinned to the elevation curve.
const segmentMarkers = computed(() => {
  const range = props.segmentRange
  if (!range) return null
  const markerFor = (distanceM: number, label: 'A' | 'B') => {
    const point = findNearestTrackProfilePoint(gradientAnalysis.value.points, distanceM)
    const clampedX = Math.max(chartRangeM.value.start, Math.min(distanceM, chartRangeM.value.end))
    return {
      label,
      x: xScale.value(clampedX),
      y: point && point.ele !== null ? yScale.value(point.ele) : null,
    }
  }
  return [markerFor(range.start, 'A'), markerFor(range.end, 'B')]
})

const selectionRangeM = computed(() => {
  if (selectionStartDistanceM.value === null || selectionEndDistanceM.value === null) return null
  return {
    start: Math.min(selectionStartDistanceM.value, selectionEndDistanceM.value),
    end: Math.max(selectionStartDistanceM.value, selectionEndDistanceM.value),
  }
})

const selectionRect = computed(() => {
  if (!selectionRangeM.value) return null
  return {
    x: xScale.value(selectionRangeM.value.start),
    width: xScale.value(selectionRangeM.value.end) - xScale.value(selectionRangeM.value.start),
  }
})

const formatDistance = (distanceM: number) =>
  distanceM >= 1000 ? `${(distanceM / 1000).toFixed(1)} km` : `${Math.round(distanceM)} m`
const formatDistanceKm = (distanceM: number) => `${(distanceM / 1000).toFixed(1)} km`

const updateCursorDistance = (distanceM: number | null) => {
  if (distanceM === null) {
    emit('update:cursorDistanceM', null)
    return
  }
  emit('update:cursorDistanceM', Math.max(0, Math.min(distanceM, totalDistanceM.value)))
}

const getDistanceFromPointer = (
  event: PointerEvent | MouseEvent,
  options?: { clampToChart?: boolean },
) => {
  const svg = svgRef.value
  if (!svg) return null
  const rect = svg.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return null
  const normalizedX = ((event.clientX - rect.left) / rect.width) * chartWidth.value
  const normalizedY = ((event.clientY - rect.top) / rect.height) * chartHeight.value
  const isWithinChartX = normalizedX >= margin.left && normalizedX <= margin.left + innerWidth.value
  const isWithinChartY = normalizedY >= margin.top && normalizedY <= margin.top + innerHeight.value
  if (!options?.clampToChart && (!isWithinChartX || !isWithinChartY)) return null
  const clampedX = Math.max(margin.left, Math.min(normalizedX, margin.left + innerWidth.value))
  return xScale.value.invert(clampedX)
}

const touchPoints = new Map<number, number>()
const isTouchPointer = (event: PointerEvent) => event.pointerType !== 'mouse'

const clearSelection = () => {
  selectionStartDistanceM.value = null
  selectionEndDistanceM.value = null
}

const syncTwoFingerSelection = () => {
  const distances = [...touchPoints.values()]
  if (distances.length < 2) return
  selectionStartDistanceM.value = Math.min(...distances)
  selectionEndDistanceM.value = Math.max(...distances)
}

const commitZoomFromSelection = () => {
  const range = selectionRangeM.value
  if (range && range.end - range.start >= MIN_ZOOM_RANGE_METERS) {
    zoomRangeM.value = range
    emit('select:range', range)
  }
  clearSelection()
}

const handleChartPointerDown = (event: PointerEvent) => {
  if (isTouchPointer(event)) {
    const distanceM = getDistanceFromPointer(event, { clampToChart: true })
    if (distanceM === null) return
    svgRef.value?.setPointerCapture(event.pointerId)
    touchPoints.set(event.pointerId, distanceM)
    if (touchPoints.size >= 2) {
      syncTwoFingerSelection()
      updateCursorDistance(null)
    } else {
      clearSelection()
      updateCursorDistance(distanceM)
    }
    return
  }
  const distanceM = getDistanceFromPointer(event)
  if (distanceM === null) return
  selectionStartDistanceM.value = distanceM
  selectionEndDistanceM.value = distanceM
  updateCursorDistance(distanceM)
}

const handleChartPointerMove = (event: PointerEvent) => {
  if (isTouchPointer(event)) {
    if (!touchPoints.has(event.pointerId)) return
    const distanceM = getDistanceFromPointer(event, { clampToChart: true })
    if (distanceM === null) return
    touchPoints.set(event.pointerId, distanceM)
    if (touchPoints.size >= 2) {
      syncTwoFingerSelection()
      updateCursorDistance(null)
    } else {
      updateCursorDistance(distanceM)
    }
    return
  }
  const isSelecting = selectionStartDistanceM.value !== null && (event.buttons & 1) === 1
  const distanceM = getDistanceFromPointer(event, { clampToChart: isSelecting })
  if (distanceM === null) {
    if (!isSelecting && props.cursorDistanceM !== null && props.cursorDistanceM !== undefined) {
      updateCursorDistance(null)
    }
    return
  }
  if (isSelecting) selectionEndDistanceM.value = distanceM
  updateCursorDistance(distanceM)
}

const handleChartPointerUp = (event: PointerEvent) => {
  if (isTouchPointer(event)) {
    const wasTwoFinger = touchPoints.size >= 2
    touchPoints.delete(event.pointerId)
    if (wasTwoFinger) {
      commitZoomFromSelection()
      updateCursorDistance(null)
    } else if (touchPoints.size === 0) {
      clearSelection()
      updateCursorDistance(null)
    }
    return
  }
  const distanceM = getDistanceFromPointer(event, { clampToChart: true })
  if (distanceM !== null) {
    updateCursorDistance(distanceM)
    if (selectionStartDistanceM.value !== null) selectionEndDistanceM.value = distanceM
  }
  commitZoomFromSelection()
}

const handleChartPointerCancel = (event: PointerEvent) => {
  if (isTouchPointer(event)) {
    touchPoints.delete(event.pointerId)
    if (touchPoints.size < 2) clearSelection()
    if (touchPoints.size === 0) updateCursorDistance(null)
    return
  }
  clearSelection()
}

const handleChartPointerLeave = (event?: PointerEvent) => {
  if (event && isTouchPointer(event)) return
  clearSelection()
  updateCursorDistance(null)
}

const resetZoomRange = () => {
  zoomRangeM.value = null
  clearSelection()
}

const enterSection = (metricId: ProfileBreakdownMetricId, categoryId: string) => {
  hoveredSection.value = { metricId, categoryId }
  emit('hover:section', { metricId, categoryId })
}

const leaveSection = () => {
  if (hoveredSection.value === null) return
  hoveredSection.value = null
  emit('hover:section', null)
}

const clickSection = (metricId: ProfileBreakdownMetricId, categoryId: string) => {
  emit('cycle:section', { metricId, categoryId })
}

const leaveProfile = () => {
  leaveSection()
  emit('leave:profile')
}

const resetLegendInteractionState = () => {
  const activeElement = document.activeElement
  if (activeElement instanceof HTMLElement && interactionScopeRef.value?.contains(activeElement)) {
    activeElement.blur()
  }
  leaveSection()
}

const syncChartWidth = () => {
  const nextWidth = chartContainerRef.value?.clientWidth ?? 0
  if (nextWidth <= 0) return
  chartWidth.value = Math.max(Math.round(nextWidth), MIN_CHART_WIDTH)
}

const scheduleChartWidthSync = (remainingFrames = 4) => {
  if (chartWidthFrameId !== null) window.cancelAnimationFrame(chartWidthFrameId)
  const run = (framesLeft: number) => {
    chartWidthFrameId = window.requestAnimationFrame(() => {
      syncChartWidth()
      if (framesLeft > 1) {
        run(framesLeft - 1)
        return
      }
      chartWidthFrameId = null
    })
  }
  run(remainingFrames)
}

const syncObservedChartContainer = () => {
  if (typeof window === 'undefined' || !('ResizeObserver' in window)) return
  if (!chartResizeObserver) {
    chartResizeObserver = new ResizeObserver(() => scheduleChartWidthSync(6))
  }
  const nextContainer = chartContainerRef.value
  if (observedChartContainer === nextContainer) return
  if (observedChartContainer) chartResizeObserver.unobserve(observedChartContainer)
  observedChartContainer = nextContainer
  if (nextContainer) {
    chartResizeObserver.observe(nextContainer)
    scheduleChartWidthSync(6)
  }
}

watch(totalDistanceM, (distanceM) => {
  if (!zoomRangeM.value) return
  if (zoomRangeM.value.start >= distanceM || zoomRangeM.value.end > distanceM) {
    zoomRangeM.value = null
  }
})

watch(
  () => props.visibleRangeM,
  (range) => {
    const current = zoomRangeM.value
    if (current?.start === range?.start && current?.end === range?.end) return
    zoomRangeM.value = range
  },
  { deep: true },
)

watch(zoomRangeM, (range) => emit('update:visibleRangeM', range))

watch(chartContainerRef, () => syncObservedChartContainer(), { flush: 'post' })

watch(
  () => props.interactionResetKey,
  (nextValue, previousValue) => {
    if (nextValue === previousValue) return
    resetLegendInteractionState()
  },
)

watch(
  () => gradientAnalysis.value.points,
  (points) => {
    if (points.length === 0) {
      emit('update:cursorDistanceM', null)
      resetZoomRange()
    }
  },
  { deep: true },
)

onMounted(() => {
  void nextTick(() => {
    scheduleChartWidthSync(6)
    syncObservedChartContainer()
  })
})

useEventListener(window, 'resize', () => {
  syncObservedChartContainer()
  scheduleChartWidthSync(8)
})

onUnmounted(() => {
  if (chartWidthFrameId !== null) {
    window.cancelAnimationFrame(chartWidthFrameId)
    chartWidthFrameId = null
  }
  chartResizeObserver?.disconnect()
  chartResizeObserver = null
  observedChartContainer = null
})

defineExpose({ resetZoomRange })
</script>

<template>
  <div ref="interactionScopeRef" class="space-y-3" @mouseleave="leaveProfile">
    <div
      ref="chartContainerRef"
      class="w-full overflow-hidden"
      :class="tall ? '' : 'rounded-xl border border-gray-200 bg-white px-0 py-1.5 shadow-sm'"
    >
      <svg
        ref="svgRef"
        :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
        class="block w-full cursor-crosshair touch-none"
        :style="{ height: `${chartHeight}px` }"
        data-testid="elevation-profile-chart"
        @pointerdown="handleChartPointerDown"
        @pointermove="handleChartPointerMove"
        @pointerup="handleChartPointerUp"
        @pointercancel="handleChartPointerCancel"
        @pointerleave="handleChartPointerLeave"
      >
        <defs>
          <linearGradient id="elevation-profile-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#bfdbfe" stop-opacity="0.9" />
            <stop offset="100%" stop-color="#eff6ff" stop-opacity="0.5" />
          </linearGradient>
        </defs>

        <g v-if="yGridlines.length">
          <g v-for="tick in yGridlines" :key="tick.value">
            <line
              :x1="margin.left"
              :x2="chartWidth - margin.right"
              :y1="tick.y"
              :y2="tick.y"
              stroke="#e2e8f0"
              stroke-width="1"
              stroke-dasharray="2 4"
            />
            <text :x="margin.left" :y="tick.y - 3" class="fill-gray-400 text-[10px]">
              {{ tick.label }}
            </text>
          </g>
        </g>

        <path v-if="areaPath" :d="areaPath" fill="url(#elevation-profile-fill)" stroke="none" />
        <path
          v-if="linePath"
          :d="linePath"
          fill="none"
          stroke="#cbd5e1"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="5"
        />

        <rect
          v-if="selectionRect && selectionRect.width > 0"
          :x="selectionRect.x"
          :y="margin.top"
          :width="selectionRect.width"
          :height="innerHeight"
          fill="#3b82f6"
          fill-opacity="0.12"
          stroke="#2563eb"
          stroke-dasharray="3 3"
          stroke-width="1"
        />

        <line
          v-for="leg in visibleLegs"
          :key="`${leg.start.index}-${leg.end.index}-${leg.categoryId}`"
          :x1="xScale(Math.max(chartRangeM.start, leg.startDistanceM))"
          :y1="leg.start.ele === null ? margin.top + innerHeight : yScale(leg.start.ele)"
          :x2="xScale(Math.min(chartRangeM.end, leg.endDistanceM))"
          :y2="leg.end.ele === null ? margin.top + innerHeight : yScale(leg.end.ele)"
          :stroke="leg.color"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="4"
        />

        <line
          v-if="activePoint"
          :x1="cursorX"
          :x2="cursorX"
          :y1="margin.top"
          :y2="margin.top + innerHeight"
          stroke="#64748b"
          stroke-dasharray="3 3"
          stroke-width="1"
          stroke-opacity="0.55"
        />
        <circle
          v-if="activePoint && cursorY !== null"
          :cx="cursorX"
          :cy="cursorY"
          r="5"
          fill="#ffffff"
          stroke="#64748b"
          stroke-width="1.75"
        />

        <g v-if="currentLocationMarker" data-testid="elevation-profile-current-location">
          <title>
            You are near {{ currentLocationMarker.distanceText }} ·
            {{ currentLocationMarker.offsetText }}
          </title>
          <line
            :x1="currentLocationMarker.x"
            :x2="currentLocationMarker.x"
            :y1="margin.top"
            :y2="margin.top + innerHeight"
            stroke="#94a3b8"
            stroke-dasharray="1.5 5"
            stroke-linecap="round"
            stroke-width="1.5"
            stroke-opacity="0.65"
          />
          <circle
            v-if="currentLocationMarker.y !== null"
            :cx="currentLocationMarker.x"
            :cy="currentLocationMarker.y"
            r="2.25"
            fill="#64748b"
            stroke="#ffffff"
            stroke-width="1.25"
          />
        </g>

        <g v-if="segmentMarkers" data-testid="elevation-profile-segment-markers">
          <g v-for="marker in segmentMarkers" :key="marker.label">
            <line
              :x1="marker.x"
              :x2="marker.x"
              :y1="margin.top"
              :y2="margin.top + innerHeight"
              stroke="#0f172a"
              stroke-width="1.25"
              stroke-opacity="0.5"
            />
            <circle
              :cx="marker.x"
              :cy="margin.top + 8"
              r="8"
              fill="#0f172a"
              stroke="#ffffff"
              stroke-width="1.5"
            />
            <text
              :x="marker.x"
              :y="margin.top + 11"
              text-anchor="middle"
              class="fill-white text-[10px] font-semibold"
            >
              {{ marker.label }}
            </text>
          </g>
        </g>

        <text x="16" :y="chartHeight - 4" class="fill-gray-500 text-[11px]">
          {{ formatDistanceKm(chartRangeM.start) }}
        </text>
        <text
          :x="chartWidth - 16"
          :y="chartHeight - 4"
          class="fill-gray-500 text-[11px]"
          text-anchor="end"
        >
          {{ formatDistanceKm(chartRangeM.end) }}
        </text>
      </svg>
    </div>

    <div
      v-if="sectionReadout"
      class="flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs"
      data-testid="elevation-profile-readout"
    >
      <span class="font-semibold text-gray-900">{{ sectionReadout.distance }}</span>
      <span v-if="sectionReadout.elevation" class="text-gray-700">{{
        sectionReadout.elevation
      }}</span>
      <span
        v-for="(chip, index) in sectionReadout.chips"
        :key="index"
        class="text-gray-700 before:mr-3 before:text-gray-300 before:content-['·']"
      >
        {{ chip }}
      </span>
    </div>

    <template v-if="showBreakdowns">
      <button
        v-if="breakdownsCollapsible"
        type="button"
        class="flex w-full items-center justify-between rounded-lg px-1 py-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500 transition-colors hover:bg-black/4 hover:cursor-pointer"
        :aria-expanded="breakdownsExpanded"
        data-testid="elevation-profile-breakdowns-toggle"
        @click="breakdownsExpanded = !breakdownsExpanded"
      >
        <span>Surface &amp; way types</span>
        <ChevronDownIcon
          class="h-4 w-4 transition-transform"
          :class="breakdownsExpanded ? 'rotate-180' : ''"
        />
      </button>

      <div
        v-for="bar in bars"
        v-show="!breakdownsCollapsible || breakdownsExpanded"
        :key="bar.metricId"
        class="space-y-1"
      >
        <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {{ bar.label }}
        </div>

        <div
          v-if="isBreakdownEmpty(bar.breakdown)"
          class="text-xs leading-5 text-gray-500"
          :data-testid="`elevation-profile-${bar.metricId}-empty`"
        >
          No {{ bar.label.toLowerCase() }} information available for this route yet.
        </div>

        <template v-else>
          <div
            class="flex h-3 w-full overflow-hidden rounded-full bg-gray-100"
            :data-testid="`elevation-profile-${bar.metricId}-bar`"
          >
            <button
              v-for="item in bar.breakdown"
              :key="item.id"
              type="button"
              class="h-full min-w-0.5 transition-opacity hover:cursor-pointer"
              :style="{
                width: `${item.percent * 100}%`,
                backgroundColor: item.color,
                opacity:
                  focusedCategoryFor(bar.metricId) === null ||
                  focusedCategoryFor(bar.metricId) === item.id
                    ? 1
                    : 0.35,
              }"
              :disabled="isCalculatingRoute"
              :title="`${item.label} · ${formatPercent(item.percent)}`"
              :aria-label="`${bar.label}: ${item.label} ${formatPercent(item.percent)}`"
              @mouseenter="enterSection(bar.metricId, item.id)"
              @mouseleave="leaveSection"
              @focus="enterSection(bar.metricId, item.id)"
              @blur="leaveSection"
              @click="clickSection(bar.metricId, item.id)"
            />
          </div>

          <div class="space-y-0.5 pt-0.5">
            <button
              v-for="item in bar.breakdown"
              :key="item.id"
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left text-sm text-gray-700 transition-colors"
              :class="
                focusedCategoryFor(bar.metricId) === item.id
                  ? 'bg-white shadow-sm'
                  : 'hover:bg-white/80'
              "
              :disabled="isCalculatingRoute"
              :data-testid="`elevation-profile-${bar.metricId}-legend-${item.id}`"
              @mouseenter="enterSection(bar.metricId, item.id)"
              @mouseleave="leaveSection"
              @focus="enterSection(bar.metricId, item.id)"
              @blur="leaveSection"
              @click="clickSection(bar.metricId, item.id)"
            >
              <span
                class="h-2.5 w-2.5 shrink-0 rounded-full"
                :style="{ backgroundColor: item.color }"
              ></span>
              <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
              <span class="shrink-0 tabular-nums text-gray-500">{{
                formatDistance(item.distanceM)
              }}</span>
              <span class="w-10 shrink-0 text-right font-semibold tabular-nums text-gray-900">{{
                formatPercent(item.percent)
              }}</span>
            </button>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>
